'use client';

import { useRef } from 'react';

import { useTrackEventAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { NaturalScrollCarousel } from '../../../../../design-system/components/natural-scroll-carousel/NaturalScrollCarousel';
import {
    ButtonTab,
    TabContent,
    TabList,
    TabRoot,
} from '../../../../../design-system/components/tabs/TabList';
import { Destination } from '../../../../../resources/destination/domain/types/destination';
import { useTrackBlockViewed } from '../../../../../shared/hooks/useTrackBlockViewed';
import { PickDomain } from '../../../../../shared/utilities/types/PickDomain';
import { DestinationCard } from '../DestinationCard/DestinationCard';

import style from './DestinationsByThematic.module.css';

type MarketingDestinationsByThematicProps = {
    destinationsByThematic: Array<{
        thematicLabel: string;
        destinations: Array<PickDomain<Destination, 'id' | 'slug' | 'name' | 'coverPicture'>>;
    }>;
};

export function MarketingDestinationsByThematic({
    destinationsByThematic,
}: MarketingDestinationsByThematicProps) {
    const { trackEvent } = useTrackEventAmplitude();
    const sectionRef = useRef<HTMLDivElement>(null);

    const firstThematicLabel = destinationsByThematic[0].thematicLabel;

    useTrackBlockViewed(sectionRef, 'destinations');

    return (
        <div ref={sectionRef} className={style.container}>
            <TabRoot
                defaultValue={firstThematicLabel}
                onValueChange={(value) => {
                    const tabIndex = destinationsByThematic.findIndex(({ thematicLabel }) => {
                        return thematicLabel === value;
                    });

                    trackEvent((ampli, defaultProperties) => {
                        ampli.thematicTabClicked({
                            ...defaultProperties,
                            tab_name: value,
                            block: 'destinations',
                            card_number: tabIndex + 1,
                        });
                    });
                }}
            >
                <div className={style.tabList}>
                    <TabList>
                        {destinationsByThematic.map(({ thematicLabel }) => {
                            return (
                                <ButtonTab key={thematicLabel} value={thematicLabel}>
                                    {thematicLabel}
                                </ButtonTab>
                            );
                        })}
                    </TabList>
                </div>

                {destinationsByThematic.map(({ thematicLabel, destinations }, thematicIndex) => {
                    return (
                        <TabContent key={thematicLabel} value={thematicLabel}>
                            <NaturalScrollCarousel
                                scrollSnapAlign="center"
                                slidesGap="4px"
                                trackingInformation={{
                                    navClick: {
                                        block: 'destinations',
                                    },
                                }}
                            >
                                {destinations.map((destination, index) => (
                                    <NaturalScrollCarousel.Slide key={index}>
                                        <DestinationCard
                                            variant="carousel"
                                            destination={destination}
                                            trackingInformation={{
                                                click: {
                                                    destination_id: destination.id,
                                                    destination_slug: destination.slug,
                                                    block: 'destinations',
                                                    card_number: index + 1,
                                                    tab_name: thematicLabel,
                                                    row_number: thematicIndex + 1,
                                                    tool_counter: 0,
                                                },
                                            }}
                                        />
                                    </NaturalScrollCarousel.Slide>
                                ))}
                            </NaturalScrollCarousel>
                        </TabContent>
                    );
                })}
            </TabRoot>
        </div>
    );
}
